/**
 * ============================================================================
 * activityFeedService.ts - ACTIVITY FEED SERVICE
 * ============================================================================
 * 
 * This service manages the global activity feed for the platform.
 * Shows recent wins, big hands, jackpots, and social activity.
 * 
 * Features:
 * - Real-time activity updates
 * - Multiple activity types (win, big_hand, jackpot, join, etc.)
 * - Privacy controls (users can opt-out)
 * - Rate limiting to prevent spam
 * 
 * ============================================================================
 */

import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  getDocs,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { hideSlots } from '../utils/storeSafe';

// ============================================================================
// TYPES
// ============================================================================

export type ActivityType =
  | 'big_win'        // Won a large pot (poker)
  | 'royal_flush'    // Hit a royal flush
  | 'straight_flush' // Hit a straight flush
  | 'four_of_kind'   // Hit quads
  | 'bad_beat'       // Lost with a strong hand
  | 'jackpot'        // Won a jackpot
  | 'bounty_72'      // Won 7-2 bounty
  | 'first_win'      // User's first win on platform
  | 'streak'         // On a winning streak
  | 'comeback'       // Big comeback from short stack
  | 'all_in_win'     // Won an all-in showdown
  | 'new_player'     // New player joined platform
  | 'level_up'       // Player reached new level
  | 'slot_big_win'   // Slot machine big win (10x+)
  | 'slot_mega_win'; // Slot machine mega win (25x+)

export interface ActivityItem {
  id: string;
  type: ActivityType;
  userId: string;
  username: string;
  photoURL?: string;
  
  // Activity details
  amount?: number;
  currency?: 'playcoins' | 'pkcash';
  handType?: string;
  tableName?: string;
  tableId?: string;
  
  // Metadata
  message: string;
  emoji: string;
  timestamp: number;
  createdAt: Timestamp;
  
  // Privacy
  isPublic: boolean;
}

export interface ActivityFeedOptions {
  maxItems?: number;
  types?: ActivityType[];
  currency?: 'playcoins' | 'pkcash' | 'all';
  includeOwn?: boolean;
}

// ============================================================================
// ACTIVITY TEMPLATES
// ============================================================================

const ACTIVITY_TEMPLATES: Record<ActivityType, { emoji: string; getMessage: (data: any) => string }> = {
  big_win: {
    emoji: '',

    getMessage: (d) => `won ${formatAmount(d.amount, d.currency)} in a big pot!`,
  },
  royal_flush: {
    emoji: '',

    getMessage: () => `hit a ROYAL FLUSH!`,
  },
  straight_flush: {
    emoji: '',

    getMessage: () => `hit a Straight Flush!`,
  },
  four_of_kind: {
    emoji: '',

    getMessage: (d) => `hit Four of a Kind${d.handType ? ` (${d.handType})` : ''}!`,
  },
  bad_beat: {
    emoji: '',

    getMessage: (d) => `suffered a bad beat with ${d.handType || 'a strong hand'}`,
  },
  jackpot: {
    emoji: '',

    getMessage: (d) => `won the JACKPOT: ${formatAmount(d.amount, d.currency)}!`,
  },
  bounty_72: {
    emoji: '',

    getMessage: (d) => `collected a 7-2 bounty of ${formatAmount(d.amount, d.currency)}!`,
  },
  first_win: {
    emoji: '',

    getMessage: () => `just won their first hand! Welcome to PSS!`,
  },
  streak: {
    emoji: '',

    getMessage: (d) => `is on a ${d.streakCount || 5}+ hand winning streak!`,
  },
  comeback: {
    emoji: '',

    getMessage: () => `made an incredible comeback from the short stack!`,
  },
  all_in_win: {
    emoji: '',

    getMessage: (d) => `won a ${formatAmount(d.amount, d.currency)} all-in showdown!`,
  },
  new_player: {
    emoji: '',

    getMessage: () => `just joined PokerSweepStars!`,
  },
  level_up: {
    emoji: '⬆',

    getMessage: (d) => `reached Level ${d.level}!`,
  },
  slot_big_win: {
    emoji: '',

    getMessage: (d) => `hit a BIG WIN of ${formatAmount(d.amount, d.currency)} on ${d.tableName || 'Slots'}!`,
  },
  slot_mega_win: {
    emoji: '',

    getMessage: (d) => `hit a MEGA WIN of ${formatAmount(d.amount, d.currency)} on ${d.tableName || 'Slots'}!`,
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatAmount(amount: number, currency?: string): string {
  if (!amount) return '';
  
  if (currency === 'pkcash') {
    return `$${amount.toFixed(2)}`;
  }
  
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(1)}K`;
  }
  
  return amount.toLocaleString();
}

function getTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  
  return new Date(timestamp).toLocaleDateString();
}

// ============================================================================
// POST ACTIVITY
// ============================================================================

/**
 *  DEPRECATED: Activities now posted server-side in Cloud Functions

 * This prevents users from posting fake wins/achievements
 */
export async function postActivity(
  type: ActivityType,
  userId: string,
  username: string,
  data: {
    amount?: number;
    currency?: 'playcoins' | 'pkcash';
    handType?: string;
    tableName?: string;
    tableId?: string;
    photoURL?: string;
    level?: number;
    streakCount?: number;
  } = {},
  isPublic: boolean = true
): Promise<string | null> {
  // No-op: Activities are now posted securely from Cloud Functions
  console.log(`⚠️ [ActivityFeed] postActivity deprecated - use server-side posting`);
  return null;
}

// ============================================================================
// SUBSCRIBE TO FEED
// ============================================================================

/**
 * Subscribe to real-time activity feed updates
 */
export function subscribeToActivityFeed(
  callback: (activities: ActivityItem[]) => void,
  options: ActivityFeedOptions = {}
): () => void {
  const {
    maxItems = 20,
    types,
    currency = 'all',
  } = options;

  console.log('📢 [ActivityFeed] Subscribing to activity feed');

  // Build query
  let q = query(
    collection(db, 'activityFeed'),
    where('isPublic', '==', true),
    orderBy('createdAt', 'desc'),
    limit(maxItems)
  );

  // Note: Firestore doesn't support multiple inequality filters
  // So we filter types client-side if needed

  return onSnapshot(q, (snapshot) => {
    let activities: ActivityItem[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as ActivityItem));

    // Client-side filtering
    if (hideSlots()) {
      // Store build: slot wins posted by website players must not surface here
      activities = activities.filter(a => !String(a.type).startsWith('slot_'));
    }
    if (types && types.length > 0) {
      activities = activities.filter(a => types.includes(a.type));
    }

    if (currency !== 'all') {
      activities = activities.filter(a => !a.currency || a.currency === currency);
    }

    callback(activities);
  }, (error) => {
    console.error('❌ [ActivityFeed] Subscription error:', error);
    callback([]);
  });
}

// ============================================================================
// GET RECENT ACTIVITIES (ONE-TIME FETCH)
// ============================================================================

/**
 * Get recent activities (one-time fetch)
 */
export async function getRecentActivities(
  options: ActivityFeedOptions = {}
): Promise<ActivityItem[]> {
  const { maxItems = 20 } = options;

  try {
    const q = query(
      collection(db, 'activityFeed'),
      where('isPublic', '==', true),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );

    const snapshot = await getDocs(q);
    
    let activities: ActivityItem[] = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as ActivityItem));
    if (hideSlots()) {
      activities = activities.filter(a => !String(a.type).startsWith('slot_'));
    }
    return activities;

  } catch (error) {
    console.error('❌ [ActivityFeed] Failed to get activities:', error);
    return [];
  }
}

// ============================================================================
// HELPER: Check if activity should be posted (thresholds)
// ============================================================================

export interface WinThresholds {
  playcoins: number;
  pkcash: number;
}

const DEFAULT_WIN_THRESHOLDS: WinThresholds = {
  playcoins: 1000,  // Post if won 1K+ PlayCoins
  pkcash: 5,        // Post if won $5+ PKCash
};

/**
 * Check if a win is big enough to post to activity feed
 */
export function isBigWin(
  amount: number,
  currency: 'playcoins' | 'pkcash',
  thresholds: WinThresholds = DEFAULT_WIN_THRESHOLDS
): boolean {
  if (currency === 'pkcash') {
    return amount >= thresholds.pkcash;
  }
  return amount >= thresholds.playcoins;
}

/**
 * Check if hand type should trigger activity post
 */
export function isNotableHand(handType: string): ActivityType | null {
  const normalized = handType.toLowerCase();
  
  if (normalized.includes('royal flush')) return 'royal_flush';
  if (normalized.includes('straight flush')) return 'straight_flush';
  if (normalized.includes('four of a kind') || normalized.includes('quads')) return 'four_of_kind';
  
  return null;
}

// ============================================================================
// UTILITY: Format activity for display
// ============================================================================

export function formatActivityForDisplay(activity: ActivityItem): {
  emoji: string;
  username: string;
  message: string;
  timeAgo: string;
  color: string;
} {
  // Color based on activity type
  const colors: Partial<Record<ActivityType, string>> = {
    royal_flush: '#fbbf24',  // Gold
    straight_flush: '#f97316', // Orange
    four_of_kind: '#a855f7',  // Purple
    jackpot: '#22c55e',       // Green
    big_win: '#22c55e',       // Green
    bounty_72: '#f59e0b',     // Amber
    bad_beat: '#ef4444',      // Red
    first_win: '#3b82f6',     // Blue
    new_player: '#06b6d4',    // Cyan
    slot_big_win: '#f59e0b',  // Amber
    slot_mega_win: '#fbbf24', // Gold
  };

  return {
    emoji: activity.emoji,
    username: activity.username,
    message: activity.message,
    timeAgo: getTimeAgo(activity.timestamp),
    color: colors[activity.type] || '#9ca3af',
  };
}

// ============================================================================
// SERVICE EXPORT
// ============================================================================

const activityFeedService = {
  // Post
  postActivity,
  
  // Subscribe
  subscribeToActivityFeed,
  getRecentActivities,
  
  // Helpers
  isBigWin,
  isNotableHand,
  formatActivityForDisplay,
};

export default activityFeedService;